import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { BrandLogo } from "@/components/brand-logo";
import {
  type DiagnosticAnswer,
  type DiagnosticContent,
  type ReportResponse,
  useDiagnosticConfig,
  useDiagnosticQuestions,
  useScoreAnswer,
} from "@/lib/api";
import { logger } from "@/lib/logger";
import { cn } from "@/lib/utils";

import {
  bootAnalytics,
  EVENT_ANSWER,
  EVENT_COMPLETE,
  EVENT_LEAD,
  EVENT_START,
  resetFiredEvents,
  track,
} from "./analytics";
import { captureAttribution } from "./attribution";
import { Composer } from "./components/composer";
import { GateForm } from "./components/gate-form";
import { Interview, type PendingAnswer } from "./components/interview";
import { Intro } from "./components/intro";
import { Portrait } from "./components/portrait";
import { PosterButton } from "./components/poster-button";

/**
 * The portrait poster download is built but hidden until Nico reworks the
 * poster design. Flip this to show "Download my portrait" on the report again,
 * and restore the poster line in `GATE_LEDE`.
 */
const SHOW_POSTER_DOWNLOAD = false;
import { Report } from "./components/report";
import { Result } from "./components/result";
import { LOAD_ERROR, LOADING } from "./copy";
import {
  isEmbedded,
  isStageLayout,
  markEmbeddedDocument,
  reportScrollTarget,
  startHeightReporting,
} from "./embed";
import {
  asTrackId,
  firstSentence,
  indexFromTracks,
  levelFor,
  resolveScore,
  scoreChips,
  type ScoredParts,
  trackScores,
  weakestTrack,
} from "./scoring";
import { resolveScrollContainer, scrollContainerTo, scrollWithin } from "./scroll";
import {
  canSendAnswer,
  type LeadSummary,
  MAX_ANSWER_CHARS,
  type Phase,
  useDiagnosticState,
} from "./state";
import {
  createAutoSend,
  MULTI_SELECT_CONTINUE_MS,
  shouldAutoContinue,
  SINGLE_SELECT_SEND_MS,
} from "./timing";
import { useReducedMotion } from "./use-reduced-motion";

/** Horizontal gutter shared by both columns (DESIGN.md). */
const GUTTER = "px-[clamp(16px,3.5vw,48px)]";

export function DiagnosticPage() {
  const { data: content, isPending, isError } = useDiagnosticQuestions();
  const { data: config } = useDiagnosticConfig();
  // Where this page is running cannot change under us, so it is read once.
  const embedded = useMemo(() => isEmbedded(), []);

  // Measurement starts as soon as the deployment settings arrive, and before
  // the questions do: a visitor who never gets past the loading line is still
  // a visit the landing page should be able to see.
  useEffect(() => {
    if (config) {
      bootAnalytics(config);
    }
  }, [config]);

  return (
    <main
      className={cn(
        "bg-hw-paper text-hw-ink font-sans [color-scheme:light]",
        // Embedded, the frame owns the height: stretching to the viewport here
        // would hold the frame open at whatever height it already has.
        !embedded && "min-h-screen",
      )}
    >
      {isPending && <PageNotice embedded={embedded}>{LOADING}</PageNotice>}
      {isError && <PageNotice embedded={embedded}>{LOAD_ERROR}</PageNotice>}
      {content && <Diagnostic content={content} embedded={embedded} />}
    </main>
  );
}

function PageNotice({ children, embedded }: { children: string; embedded: boolean }) {
  return (
    <div
      className={cn(
        "flex items-center",
        // Embedded from 900px the notice is already the stage's height, so the
        // frame does not change size when the diagnostic replaces it.
        embedded ? "min-h-[240px] min-[900px]:h-[680px]" : "min-h-screen",
        GUTTER,
      )}
    >
      <p className="text-hw-soft text-[15px]" role="status">
        {children}
      </p>
    </div>
  );
}

function Diagnostic({ content, embedded }: { content: DiagnosticContent; embedded: boolean }) {
  const { state, dispatch, reset } = useDiagnosticState();
  const reducedMotion = useReducedMotion();
  // `mutateAsync` is stable across renders; the result object is not, so only
  // the function is held onto here.
  const { mutateAsync: scoreAnswer } = useScoreAnswer();

  const [selected, setSelected] = useState<number[]>([]);
  const [text, setText] = useState("");
  // The lock itself lives in the ref: it is read and written synchronously at
  // the top of `send`, so two sends in the same tick cannot both get through.
  // The state is only a mirror, for the disabled styling.
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  // The answer on screen while `/score` is in flight: its bubble plus the dots.
  const [pending, setPending] = useState<PendingAnswer | null>(null);
  // True while the multi-select question is counting down to sending itself.
  const [autoContinuing, setAutoContinuing] = useState(false);

  // One timer for both self-sends: a question is either single or multi, so
  // the two can never be waiting at the same time.
  const autoSend = useRef(createAutoSend()).current;
  // The composer's live values, readable from a timer that fired after the
  // render that set them.
  const textRef = useRef(text);
  textRef.current = text;
  const selectedRef = useRef(selected);
  selectedRef.current = selected;
  // True once an answer has been sent in this session. Focus is only moved
  // after a real interaction, never on a restore from sessionStorage.
  const answeredOnceRef = useRef(false);
  // The blocks the page scrolls to when the interview ends.
  const resultRef = useRef<HTMLDivElement>(null);
  const reportRef = useRef<HTMLDivElement>(null);
  // The containers that scroll: the chat, the standalone right column, and the
  // embedded panel that holds the gate and then the report.
  const chatRef = useRef<HTMLDivElement>(null);
  const columnRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLElement>(null);
  // The phase the scroll handling last acted on. It starts at whatever phase
  // the page mounted in, so a restored session is not a transition.
  const settledPhaseRef = useRef<Phase>(state.phase);

  const cancelAutoSend = useCallback(() => {
    autoSend.cancel();
    setAutoContinuing(false);
  }, [autoSend]);

  // The campaign that brought the visitor here is read from the landing URL
  // once, on mount, and kept for the gate to submit minutes later.
  useEffect(() => {
    captureAttribution();
  }, []);

  // Never let a pending self-send fire into an unmounted page.
  useEffect(() => () => autoSend.cancel(), [autoSend]);

  // Inside a parent page the frame cannot size itself, so the height goes over
  // postMessage and `public/embed.js` applies it.
  useEffect(() => {
    if (!embedded) {
      return;
    }
    logger.debug("[embed]", "running inside a parent page");
    const unmark = markEmbeddedDocument();
    const stop = startHeightReporting();
    return () => {
      stop();
      unmark();
    };
  }, [embedded]);

  const questions = content.questions;
  const questionCount = questions.length;
  const question = questions[Math.min(state.step, questionCount - 1)];

  // A restored session is only resumable against the questions we actually
  // hold: more answers than questions, or a result that is short of the last
  // question, would render a wrong index and leave no way forward. Drop it and
  // send the visitor back to the intro rather than stranding them.
  const { phase, step, answers } = state;
  useEffect(() => {
    const finished = phase === "result" || phase === "report";
    const unusable =
      answers.length > questionCount ||
      (finished && answers.length !== questionCount) ||
      (phase === "interview" && step >= questionCount);
    if (!unusable) {
      return;
    }
    logger.warn("[diagnostic]", "saved session does not fit these questions, starting over", {
      phase,
      step,
      answers: answers.length,
      questions: questionCount,
    });
    // Nothing of this visit survives, so neither should the record of which
    // events it reported: the funnel starts again from the intro.
    resetFiredEvents();
    reset();
  }, [phase, step, answers.length, questionCount, reset]);

  // A timer that belongs to the question on screen has no business outliving
  // the interview.
  useEffect(() => {
    if (phase !== "interview") {
      cancelAutoSend();
    }
  }, [phase, cancelAutoSend]);

  /**
   * The result and the report both render below the portrait on their own
   * page, which on a tall screen is the whole column. Neither announces
   * itself, so the column scrolls to it and the heading takes focus. A
   * restored session lands there too, without the animation: nothing just
   * happened, so nothing should move.
   *
   * Embedded, the result and the report swap into panels instead, so the
   * frame only resets the panel. From 900px those panels are a fixed 680px
   * stage the visitor is already looking at, so the parent page is never
   * asked to move: asking there is what made the landing page jump. Below
   * 900px the panels stack and the result lands under the chat, so the parent
   * is asked to scroll once per move into the result and once into the
   * report, a frame after the swap so the layout it measures is the final
   * one; never per answer and never on a restore.
   */
  useEffect(() => {
    const target =
      phase === "report" ? reportRef.current : phase === "result" ? resultRef.current : null;
    if (!target) {
      settledPhaseRef.current = phase;
      return;
    }
    const entered = settledPhaseRef.current !== phase;
    const frame = requestAnimationFrame(() => {
      settledPhaseRef.current = phase;
      if (embedded) {
        const panel = panelRef.current;
        if (panel) {
          scrollContainerTo(panel, 0, "auto");
        }
        // A cross-origin parent cannot be scrolled from in here, so it is asked.
        if (entered && !isStageLayout()) {
          logger.debug("[embed]", "asking the parent to scroll", { phase });
          reportScrollTarget(target);
        }
        return;
      }
      const smooth = !reducedMotion && answeredOnceRef.current;
      const container = resolveScrollContainer(columnRef.current, false);
      if (container) {
        scrollWithin(container, target, smooth ? "smooth" : "auto");
      }
    });
    return () => cancelAnimationFrame(frame);
  }, [phase, reducedMotion, embedded]);

  // A gate field that failed validation takes focus without scrolling, and is
  // then brought into view in whichever container holds the form.
  const revealField = useCallback(
    (field: HTMLElement) => {
      const container = embedded
        ? panelRef.current
        : resolveScrollContainer(columnRef.current, false);
      if (container) {
        scrollWithin(container, field, reducedMotion ? "auto" : "smooth", {
          align: "nearest",
          margin: 24,
        });
      }
    },
    [embedded, reducedMotion],
  );

  const tracks = useMemo(() => trackScores(state.answers, content), [state.answers, content]);
  const index = useMemo(() => indexFromTracks(tracks), [tracks]);

  const send = useCallback(
    async (chipIndices: number[]) => {
      if (!question || busyRef.current || state.phase !== "interview") {
        return;
      }
      const typed = textRef.current.trim().slice(0, MAX_ANSWER_CHARS);
      const chips = chipIndices
        .map((i) => question.options[i]?.label)
        .filter((label): label is string => Boolean(label));

      if (!canSendAnswer(question.multi, chips.length > 0)) {
        return;
      }

      busyRef.current = true;
      setBusy(true);
      cancelAutoSend();

      try {
        // The chips carry the score on their own, so every answer has a local
        // reading before anything is sent. Typed context can only sharpen it.
        const chipResult = scoreChips(question, chips, content);
        const local: ScoredParts = {
          score: chipResult.score,
          evidence: firstSentence(typed || chips.join("; ")),
          reflection: chipResult.reflection || content.defaultReflection,
          source: "chip",
        };

        let scored = local;
        if (typed.length > 0) {
          setPending({ chips, text: typed });
          try {
            const response = await scoreAnswer({
              qid: question.id,
              chips,
              text: typed,
            });
            scored = resolveScore(local, response);
          } catch (error) {
            logger.warn("[diagnostic] score fallback", { qid: question.id }, error);
            scored = local;
          } finally {
            setPending(null);
          }
        }

        const answer: DiagnosticAnswer = {
          qid: question.id,
          track: asTrackId(question.track),
          chips,
          text: typed,
          ...scored,
        };

        logger.debug("[diagnostic]", "scored answer", {
          qid: answer.qid,
          source: answer.source,
          score: answer.score,
          chips: chips.length,
          chars: typed.length,
        });

        track(EVENT_ANSWER, {
          qid: answer.qid,
          source: answer.source,
          has_text: typed.length > 0,
        });

        answeredOnceRef.current = true;
        dispatch({ type: "answer", answer, questionCount: questions.length });
        setSelected([]);
        selectedRef.current = [];
        setText("");
        textRef.current = "";

        if (state.answers.length + 1 >= questions.length) {
          logger.debug("[diagnostic]", "interview complete");
        }
      } finally {
        busyRef.current = false;
        setBusy(false);
      }
    },
    [
      cancelAutoSend,
      content,
      dispatch,
      question,
      questions.length,
      scoreAnswer,
      state.answers.length,
      state.phase,
    ],
  );

  const toggleChip = useCallback(
    (optionIndex: number) => {
      if (!question || busyRef.current) {
        return;
      }

      if (!question.multi) {
        setSelected([optionIndex]);
        selectedRef.current = [optionIndex];
        // A single-select chip with an empty textarea answers the question on
        // its own, so it sends itself after a beat instead of asking for a
        // second tap.
        if (textRef.current.trim().length === 0) {
          autoSend.schedule(() => {
            if (textRef.current.trim().length === 0) {
              logger.debug("[diagnostic]", "auto-sending single-select chip", {
                qid: question.id,
                optionIndex,
              });
              void send([optionIndex]);
            }
          }, SINGLE_SELECT_SEND_MS);
        }
        return;
      }

      const current = selectedRef.current;
      const next = current.includes(optionIndex)
        ? current.filter((i) => i !== optionIndex)
        : [...current, optionIndex];
      setSelected(next);
      selectedRef.current = next;

      // The multi-select question sends itself too, but slowly, and every tick
      // restarts the wait. Unticking the last chip stops it: an empty answer
      // there means "none of these", which the visitor has to say themselves.
      if (!shouldAutoContinue(next.length, textRef.current)) {
        cancelAutoSend();
        return;
      }
      setAutoContinuing(true);
      autoSend.schedule(() => {
        setAutoContinuing(false);
        if (!shouldAutoContinue(selectedRef.current.length, textRef.current)) {
          return;
        }
        logger.debug("[diagnostic]", "auto-continuing multi-select", {
          qid: question.id,
          chips: selectedRef.current.length,
        });
        void send(selectedRef.current);
      }, MULTI_SELECT_CONTINUE_MS);
    },
    [autoSend, cancelAutoSend, question, send],
  );

  // Typing is the visitor still answering: whatever was about to send itself
  // waits for them to finish and press the button.
  const changeText = useCallback(
    (value: string) => {
      setText(value);
      textRef.current = value;
      cancelAutoSend();
    },
    [cancelAutoSend],
  );

  const showReport = useCallback(
    (lead: LeadSummary, report: ReportResponse) => {
      logger.debug("[diagnostic]", "report ready", {
        source: report.source,
        lead: report.lead_status,
      });
      // The level, and nothing else: the gate has just collected a name, an
      // email and a company, none of which belongs in an analytics event.
      track(EVENT_LEAD, { level: report.level });
      dispatch({ type: "report", lead, report });
    },
    [dispatch],
  );

  const start = useCallback(() => {
    logger.debug("[diagnostic]", "interview started");
    track(EVENT_START);
    answeredOnceRef.current = false;
    setSelected([]);
    selectedRef.current = [];
    setText("");
    textRef.current = "";
    dispatch({ type: "start" });
  }, [dispatch]);

  // Until the gate is submitted these are the local readings. Once the report
  // comes back they are the server's, which recomputes every score from the
  // answers: two screens on top of each other must never show two numbers.
  const report = state.report;
  const shownIndex = report ? report.index : index;
  const shownLevel = report ? report.level : levelFor(index, content);
  const weakest = report ? asTrackId(report.weakest_track) : weakestTrack(tracks);
  // `inSentence` keeps acronyms cased ("AI-first operating model") where a
  // lowercased `name` would not.
  const weakestInSentence = content.tracks[weakest]?.inSentence ?? "";
  const finished = state.phase === "result" || state.phase === "report";

  // The result is a milestone, not a render: the one-shot guard inside `track`
  // keeps a re-render, a refresh and a restored session down to one event.
  useEffect(() => {
    if (finished) {
      track(EVENT_COMPLETE, { index: shownIndex, level: shownLevel });
    }
  }, [finished, shownIndex, shownLevel]);

  // The pieces both layouts are built from. Each layout renders each piece at
  // most once, so nothing here is ever mounted twice.
  const title = <h1 className="sr-only">How hard-wired is your organization?</h1>;

  const chat =
    state.phase === "intro" ? (
      <Intro onStart={start} embedded={embedded} />
    ) : (
      <>
        {/* The intro owns the visible h1; once it is gone the page still
            needs one, so the interview and the result carry it. */}
        {title}
        <Interview
          content={content}
          answers={state.answers}
          step={state.step}
          phase={state.phase}
          selected={selected}
          locked={busy}
          onToggleChip={toggleChip}
          autoContinuing={autoContinuing}
          pending={pending}
          takeFocus={answeredOnceRef.current}
          reducedMotion={reducedMotion}
          pinCurrentQuestion={embedded}
          scrollContainerRef={chatRef}
        />
      </>
    );

  const composer = state.phase === "interview" && question && (
    <div
      className={cn("border-hw-line bg-hw-paper shrink-0 border-t py-4 min-[900px]:py-5", GUTTER)}
    >
      <Composer
        multi={question.multi}
        text={text}
        onTextChange={changeText}
        onSend={() => void send(selected)}
        busy={busy}
        hasChips={selected.length > 0}
      />
    </div>
  );

  const portrait = (
    <Portrait
      content={content}
      answers={state.answers}
      compact={finished}
      embedded={embedded}
      reducedMotion={reducedMotion}
    />
  );

  const result = finished && (
    <div ref={resultRef}>
      <Result
        index={shownIndex}
        level={shownLevel}
        weakestTrackInSentence={weakestInSentence}
        takeFocus={answeredOnceRef.current}
        reducedMotion={reducedMotion}
        embedded={embedded}
      />
    </div>
  );

  const gate = state.phase === "result" && (
    <GateForm answers={state.answers} onReport={showReport} revealField={revealField} />
  );

  const written = state.phase === "report" && state.report && state.lead && (
    <div ref={reportRef}>
      <Report firstName={state.lead.firstName} report={state.report} tracks={content.tracks} />
      {SHOW_POSTER_DOWNLOAD && (
        <PosterButton
          answers={state.answers}
          company={state.lead.company ?? ""}
          content={content}
          index={state.report.index}
          level={state.report.level}
          perTrack={state.report.per_track}
          tracks={content.tracks}
        />
      )}
    </div>
  );

  if (embedded) {
    /*
     * Embedded: a stage whose height never changes after load from 900px up,
     * so the frame around it never grows. Three panels sit in a two-column
     * grid and swap columns when the interview ends: the chat leaves, the
     * portrait (with the result under it) moves left, and the gate, then the
     * report, takes the right. DOM order never changes, so the portrait is
     * the same mounted component throughout and keeps its drawn bands.
     *
     * Below 900px the panels stack. The chat is still a fixed-height box, so
     * answering never changes the height; it only changes at the result and
     * at the report.
     */
    return (
      <div className="min-[900px]:grid min-[900px]:h-[680px] min-[900px]:grid-cols-2 min-[900px]:grid-rows-1 min-[900px]:overflow-hidden">
        {!finished && (
          <section
            aria-label="Your interview"
            className="border-hw-line flex min-w-0 flex-col min-[900px]:col-start-1 min-[900px]:row-start-1 min-[900px]:min-h-0 min-[900px]:border-r"
          >
            <div
              ref={chatRef}
              className={cn(
                "py-8",
                GUTTER,
                "min-[900px]:min-h-0 min-[900px]:flex-1 min-[900px]:overflow-y-auto min-[900px]:overscroll-contain",
                // The chat scrolls inside a box sized in pixels: a `vh` here
                // would be the frame's own height, which is what it is feeding.
                state.phase !== "intro" &&
                  "h-[420px] overflow-y-auto overscroll-contain min-[900px]:h-auto",
              )}
            >
              {chat}
            </div>
            {composer}
          </section>
        )}

        <section
          className={cn(
            "flex min-w-0 flex-col min-[900px]:row-start-1 min-[900px]:min-h-0",
            finished
              ? "border-hw-line motion-safe:animate-hw-fade min-[900px]:col-start-1 min-[900px]:overflow-y-auto min-[900px]:overscroll-contain min-[900px]:border-r"
              : "min-[900px]:col-start-2",
          )}
        >
          <div
            className={cn(
              "flex flex-col py-8",
              GUTTER,
              !finished && "min-[900px]:min-h-0 min-[900px]:flex-1",
            )}
          >
            {finished && title}
            {portrait}
            {result}
          </div>
        </section>

        {finished && (
          <section
            // A new key per phase: the report arrives as a fresh panel, at its
            // top and fading in, rather than as the gate panel's contents.
            key={state.phase}
            ref={panelRef}
            className="motion-safe:animate-hw-fade min-w-0 min-[900px]:col-start-2 min-[900px]:row-start-1 min-[900px]:min-h-0 min-[900px]:overflow-y-auto min-[900px]:overscroll-contain"
          >
            <div className={cn("pb-8 min-[900px]:pt-2", GUTTER)}>
              {gate}
              {written}
            </div>
          </section>
        )}
      </div>
    );
  }

  return (
    <div className="min-[900px]:grid min-[900px]:h-screen min-[900px]:grid-cols-2 min-[900px]:overflow-hidden">
      {/* Left column: the interview. On its own page the two columns are the
          viewport and scroll inside it. */}
      <section
        aria-label="Your interview"
        className="border-hw-line flex min-w-0 flex-col min-[900px]:h-screen min-[900px]:min-h-0 min-[900px]:border-r"
      >
        <header className={cn("border-hw-line flex h-16 shrink-0 items-center border-b", GUTTER)}>
          <BrandLogo name="Board of Innovation" className="h-5" />
        </header>

        <div
          ref={chatRef}
          className={cn(
            "py-8",
            GUTTER,
            "min-[900px]:min-h-0 min-[900px]:flex-1 min-[900px]:overflow-y-auto",
          )}
        >
          {chat}
        </div>

        {composer}
      </section>

      {/* Right column: the portrait and the result. */}
      <section className="flex min-w-0 flex-col min-[900px]:h-screen min-[900px]:min-h-0">
        <header
          className={cn(
            "border-hw-line flex h-16 shrink-0 items-center justify-end border-b",
            GUTTER,
          )}
        >
          <span className="hw-mono">Hard-Wired · AI diagnostic</span>
        </header>

        <div
          ref={columnRef}
          className={cn(
            "py-8",
            GUTTER,
            "min-[900px]:min-h-0 min-[900px]:flex-1 min-[900px]:overflow-y-auto min-[900px]:overscroll-contain",
          )}
        >
          {portrait}
          {result}
          {gate}
          {written}
        </div>
      </section>
    </div>
  );
}
